import {
  MAX_STAMINA,
  SKIP_STAMINA_COST,
  SKIP_WINDOW_TICKS,
  WALK_SPEED,
  clamp,
  clockGlyph,
  dayPhrase,
  timeOfDay,
} from '@stillsun/shared';
import type { PlayerState, Vec3 } from '@stillsun/shared';
import type { ConnState } from '../net/client.js';
// v3 took `ExtractSnap` off the wire with the rest of the raid loop; the client keeps its own
// shape for the compass pins the city and the zone still put on the ring.
import type { ExtractSnap } from '../render/beacons.js';
// Live render-resolution readout for the stats line below. No reference to the renderer
// instance reaches Hud, so this reads the shared object the renderer keeps updated.
import { gfxStats } from '../render/renderer.js';
import { icon } from './icons.js';

const NAME_KEY = 'stillsun.name';
const MAX_CHAT_LINES = 8;
const HIT_MARKER_MS = 130;
/** How long the SKIP tick mark flashes for once a skip-jump is detected. */
const SKIP_FLASH_MS = 200;
/** Stamina bar fades out after sitting full and untouched this long. */
const STAMINA_IDLE_MS = 2000;
/** Circumference of the revive ring in index.html (r = 19). */
const REVIVE_RING_LEN = 2 * Math.PI * 19;
/** How often the world clock is re-read. Eight phrases in four hours; 1 Hz is generous. */
const WORLD_CLOCK_MS = 1000;

/**
 * The halo's compass arc. `COMPASS_HALF_SPAN_DEG` is how much bearing it shows each way
 * from dead ahead: ±90° puts two cardinal letters on the arc either side of the top, and
 * makes a quarter turn of the head walk a letter from the top to an end. That span is
 * squeezed onto `COMPASS_ARC_DEG` of the ring so the arc never reaches the health and
 * stamina arcs on the flanks.
 */
const COMPASS_HALF_SPAN_DEG = 90;
/** Half the arc, in ring degrees. Keep in step with --halo-span in index.html. */
const COMPASS_ARC_DEG = 70;
/** Letters at the quarters, a bare tick at the 45s — NE/SE/SW/NW spelled out is noise. */
const COMPASS_POINTS = ['N', '', 'E', '', 'S', '', 'W', ''] as const;
/** A little past the edge before a cardinal is dropped, so it does not blink at the border. */
const COMPASS_CARD_SLACK_DEG = 4;
/** Never let the arc flood: the nearest few Wakers only. */
const COMPASS_MAX_WAKERS = 6;
/** The server only ever lights two extracts, but the arc does not depend on that. */
const COMPASS_MAX_EXITS = 4;
/** Pool size: every marker the arc can ever draw at once, built once at boot. */
const COMPASS_MARKS = COMPASS_MAX_EXITS + COMPASS_MAX_WAKERS;

const DEG = 180 / Math.PI;

/**
 * The halo (B3 · Halo + WoW bar). Three states, and the ring is asleep in most of them:
 *
 *   asleep  at rest — a hairline at 35%, no numbers
 *   awake   for HALO_WAKE_MS after damage taken, an ability coming off cooldown, a weapon
 *           swap or reload, or a sprint that is actually draining stamina; and held for
 *           as long as health is under HALO_LOW_HP
 *   gone    down the sights, and whenever the raid HUD is not live at all
 *
 * The state is a pure function of (aiming, health, now, wakeUntil) — see `haloState` — so
 * it can never get stuck on, and the class is written only when it actually changes.
 */
const HALO_WAKE_MS = 3000;
const HALO_LOW_HP = 30;
export type HaloState = 'asleep' | 'awake' | 'gone';
/** Half the semicircle path length in index.html's #haloring: π × r, r = 110. */
const HALO_ARC_LEN = Math.PI * 110;
/** Damage numbers by the reticle: pooled, round-robin, never more than this on screen. */
const HALO_DMG_POOL = 6;

/** Wraps an angle in degrees into (-180, 180]. */
function normDeg(deg: number): number {
  let a = deg % 360;
  if (a > 180) a -= 360;
  else if (a <= -180) a += 360;
  return a;
}

/**
 * Swings a zero-size node round the compass arc and stands it back up. The node is pinned
 * to the ring centre by CSS, so `rotate(θ) translateY(-r) rotate(-θ)` is the whole job —
 * one transform string, no layout, and the label stays upright at every bearing.
 */
function arcTransform(deg: number): string {
  // Negate the number, never the string: a bearing left of centre is negative, and
  // `rotate(--70deg)` is not an angle — the browser throws the whole declaration away
  // and the mark stays wherever it was, which is the reticle.
  return `rotate(${deg.toFixed(1)}deg) translateY(calc(var(--halo-cr) * -1)) rotate(${(-deg).toFixed(1)}deg)`;
}

/** One live remote as the compass reads it — see RemoteManager.compassTargets. */
export interface CompassTarget {
  id: number;
  pos: Vec3;
}

/** A pooled cardinal letter / 45° tick. `lastX` is the last arc angle written, in degrees. */
interface CompassCard {
  el: HTMLElement;
  lastX: number;
  shown: boolean;
}

/** A pooled extract / Waker marker. Everything cached here gates a DOM write. */
interface CompassMark {
  root: HTMLDivElement;
  pip: HTMLSpanElement;
  lastX: number;
  lastId: number;
  exit: boolean;
  edge: boolean;
  shown: boolean;
}

/** The slice of PlayerState the movement HUD (stamina/winded/skip/stance) actually reads. */
export type MovementSnapshot = Pick<
  PlayerState,
  'stamina' | 'winded' | 'grounded' | 'groundTicks' | 'vel' | 'stance'
>;

function need<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`[stillsun] missing #${id} in index.html`);
  return el as T;
}

export interface StatsView {
  fps: number;
  ping: number;
  tick: number;
  players: number;
  backend: string;
}

/**
 * What the target frame draws. Assembled by main.ts from whichever side of the world
 * `input.targetId` names — an entity, another Waker, or the player themselves (a Warden's
 * friendly abilities default to self, so selecting your own body is a real thing to do).
 *
 * Handed in as one reused object: the frame loop fills the same scratch every frame and the
 * HUD keeps no reference to it.
 */
export interface TargetView {
  /** A Hymn's kind name, or a player's name. */
  name: string;
  /** 0 hides the level, which is what an unlevelled thing (a loot bag) should read as. */
  level: number;
  hp: number;
  maxHp: number;
  /** Another Waker rather than one of the Choir: the bar goes cyan. */
  friendly: boolean;
  /** Milliseconds left on this target's cast; 0 = not casting, and the bar is gone. */
  castMs: number;
  /** 0 at the start of that cast, 1 as it lands. */
  castFraction: number;
  /** What to call it. `Ev.Cast` names an ability; a Hymn's wind-up has no name on the wire. */
  castName: string;
}

/** One row of the Tab scoreboard. */
export interface ScoreRow {
  id: number;
  name: string;
  kills: number;
  deaths: number;
}

export class Hud {
  readonly canvas = need<HTMLCanvasElement>('game');
  readonly startModal = need<HTMLDivElement>('start');
  readonly startForm = need<HTMLFormElement>('startform');
  readonly nameInput = need<HTMLInputElement>('name');
  readonly enterButton = need<HTMLButtonElement>('enter');
  readonly bootLine = need<HTMLDivElement>('boot');
  readonly chatInput = need<HTMLInputElement>('chatinput');
  /** The WoW bar. ui/raidbar.ts owns everything inside it; this is only the handle. */
  readonly raidBarRoot = need<HTMLDivElement>('raidbar');
  /** ui/killfeed.ts pools its rows into this. */
  readonly killFeedRoot = need<HTMLDivElement>('killfeed');
  readonly hubRoot = need<HTMLDivElement>('hub');
  /** The Waking's own surface: shown instead of the hub, never over it. */
  readonly wakingRoot = need<HTMLDivElement>('waking');
  readonly hubBackdrop = need<HTMLDivElement>('hubbg');
  readonly overlayRoot = need<HTMLDivElement>('overlay');
  readonly markerRoot = need<HTMLDivElement>('worldmarkers');

  private readonly statsEl = need<HTMLDivElement>('stats');
  private readonly bannerEl = need<HTMLDivElement>('banner');
  private readonly chatLogEl = need<HTMLDivElement>('chatlog');

  private readonly healthBarEl = need<HTMLDivElement>('healthbar');
  private readonly healthFillEl = need<HTMLDivElement>('healthfill');
  private readonly healthNumEl = need<HTMLDivElement>('healthnum');
  private readonly staminaBarEl = need<HTMLDivElement>('staminabar');
  private readonly staminaFillEl = need<HTMLDivElement>('staminafill');
  private readonly skipTickEl = need<HTMLSpanElement>('skiptick');
  private readonly sprintTickEl = need<HTMLSpanElement>('sprinttick');
  private readonly stanceIconEl = need<HTMLDivElement>('stanceicon');
  private readonly callingNameEl = need<HTMLSpanElement>('callingname');
  private readonly crosshairEl = need<HTMLDivElement>('crosshair');
  private readonly windedFxEl = need<HTMLDivElement>('windedfx');
  private readonly vignetteEl = need<HTMLDivElement>('vignette');
  private readonly hitMarkerEl = need<HTMLDivElement>('hitmarker');
  private readonly hitPulseEl = need<HTMLDivElement>('hitpulse');
  private readonly deathEl = need<HTMLDivElement>('death');
  private readonly deathTitleEl = need<HTMLDivElement>('deathtitle');
  private readonly deathCountEl = need<HTMLDivElement>('deathcount');
  private readonly killCamEl = need<HTMLDivElement>('killcam');
  private readonly killCamTitleEl = need<HTMLDivElement>('killcamtitle');
  private readonly killCamMetaEl = need<HTMLDivElement>('killcammeta');
  private readonly killCamFillEl = need<HTMLDivElement>('killcamfill');
  private readonly scoreboardEl = need<HTMLDivElement>('scoreboard');
  private readonly scoreRowsEl = need<HTMLDivElement>('scorerows');

  // M3: ability grades, downed/revive, and the raid boss bar.
  private readonly abilityFxEl = need<HTMLDivElement>('abilityfx');
  private readonly downedFxEl = need<HTMLDivElement>('downedfx');
  private readonly downedEl = need<HTMLDivElement>('downed');
  private readonly downedSubEl = need<HTMLDivElement>('downedsub');
  private readonly downedFillEl = need<HTMLDivElement>('downedfill');
  private readonly reviveEl = need<HTMLDivElement>('revive');
  private readonly reviveTextEl = need<HTMLSpanElement>('revivetext');
  private readonly reviveRingEl = document.querySelector<SVGCircleElement>('#revivering .fg');
  // The world clock (top-centre). Latched like everything else here: each field records what
  // was last written so a 1 Hz refresh only touches the DOM when the hour has actually moved.
  private readonly worldClockEl = need<HTMLDivElement>('worldclock');
  private readonly worldClockPhraseEl = need<HTMLSpanElement>('wcphrase');
  private readonly worldClockFillEl = need<HTMLDivElement>('wcdayfill');
  private worldClockOn = false;
  private worldClockAt = -1e9;
  private worldClockPhrase = '';
  private worldClockNight = false;
  private worldClockFill = -1;

  private readonly bossBarEl = need<HTMLDivElement>('bossbar');
  private readonly bossNameEl = need<HTMLDivElement>('bossname');
  private readonly bossFillEl = need<HTMLDivElement>('bossfill');

  // Phase 2b: the target frame, fourth in the top-centre stack.
  private readonly targetFrameEl = need<HTMLDivElement>('targetframe');
  private readonly targetNameEl = need<HTMLSpanElement>('targetname');
  private readonly targetLvlEl = need<HTMLSpanElement>('targetlvl');
  private readonly targetFillEl = need<HTMLDivElement>('targetfill');
  private readonly targetCastFillEl = need<HTMLDivElement>('targetcastfill');
  private readonly targetCastNameEl = need<HTMLSpanElement>('targetcastname');

  // The halo. Every node is built once in the constructor and reused forever; the frame
  // loop only ever writes a transform, a dash offset or a textContent, and only when the
  // value it draws has actually moved. Nothing in `updateHalo` allocates.
  private readonly haloEl = need<HTMLDivElement>('halo');
  private readonly haloHpEl = document.querySelector<SVGPathElement>('#halohp');
  private readonly haloStamEl = document.querySelector<SVGPathElement>('#halostam');
  private readonly haloExitEl = need<HTMLDivElement>('haloexit');
  private readonly haloDmgEl = need<HTMLDivElement>('halodmg');
  private readonly haloPipEls: HTMLSpanElement[] = [];
  private readonly haloDmgPool: HTMLSpanElement[] = [];
  private haloDmgNext = 0;
  private haloState: HaloState | '' = '';
  /** performance.now() the wake latch runs out at, and the forced state for the probe. */
  private haloWakeUntil = 0;
  private haloForced: HaloState | null = null;
  private haloAiming = false;
  private haloLive = false;
  private haloHpFrac = -1;
  private haloStamFrac = -1;
  private readonly haloPipReady = [false, false, false];
  private lastHaloExit = '';

  private readonly compassEl = need<HTMLDivElement>('halocompass');
  private readonly compassCards: CompassCard[] = [];
  private readonly compassMarks: CompassMark[] = [];
  /** Live extracts, flattened out of the last `ExtractSnap` list so the frame loop reads plain
   * numbers. */
  private readonly compassExits: { id: number; x: number; y: number; z: number }[] = [];
  /**
   * What the map calls each extract. The snapshot only carries ids, so the halo used to
   * read "exit 1" while the zone map, off the same data, read "The Ferry" — two screens
   * disagreeing about the name of the one place you are running to. Seeded from `MapDef`
   * whenever the map changes; an id with no name still falls back to its number.
   */
  private readonly extractNames = new Map<number, string>();
  private compassExitCount = 0;
  /** Fixed-size nearest-N buffer for the Waker markers; sorted by distance, never grown. */
  private readonly compassPick: { id: number; dist: number; rel: number }[] = [];

  /** Vignette + hit-marker intensities, decayed every frame by `tickEffects`. */
  private vignette = 0;
  private hitMarkerUntil = 0;
  private lastHealth = -1;

  /** performance.now() the stamina bar last read MAX_STAMINA; -1 while not full. */
  private staminaFullSince = -1;
  private skipFlashUntil = 0;
  private prevGrounded = false;
  private prevGroundTicks = 0;
  private prevHSpeed = 0;
  /** Last frame's stamina and the latched SPRINT mark it drives. */
  private prevStamina = MAX_STAMINA;
  private sprintOn = false;
  private lastStance = -1;
  private lastCallingName = '';
  /** Last quantised kill-cam bar fraction written to the DOM; -1 = nothing written. */
  private killCamProgress = -1;

  // M3 latch state — every setter below is called every frame, so each one only ever
  // touches the DOM when the value it draws has actually changed.
  private abilityGrade: 'surge' | 'veil' | 'silence' | null = null;
  private downedOn = false;
  private reviveOn = false;
  private lastReviveLabel = '';
  private lastReviveOffset = -1;
  private bossOn = false;
  private lastBossName = '';
  private lastBossFrac = -1;
  private targetOn = false;
  private targetFriendly = false;
  private targetDead = false;
  private targetCasting = false;
  private lastTargetName = '';
  private lastTargetLvl = '';
  private lastTargetFrac = -1;
  private lastTargetCastName = '';
  private lastTargetCastFrac = -1;

  constructor() {
    this.buildHalo();
  }

  // ------------------------------------------------------------------- halo

  /**
   * Builds the halo's whole DOM up front: the eight cardinal nodes (a letter at each
   * quarter, a tick at each 45), the extract/Waker marker pool and the damage-number pool.
   * Appended into #halocompass / #halodmg, which index.html leaves empty.
   */
  private buildHalo(): void {
    for (let i = 0; i < COMPASS_POINTS.length; i++) {
      const letter = COMPASS_POINTS[i] ?? '';
      const el = document.createElement('div');
      if (letter === '') {
        el.className = 'ctick off';
      } else {
        el.className = letter === 'N' ? 'ccard north off' : 'ccard off';
        el.textContent = letter;
      }
      this.compassEl.append(el);
      this.compassCards.push({ el, lastX: Number.NaN, shown: false });
    }

    for (let i = 0; i < COMPASS_MARKS; i++) {
      const root = document.createElement('div');
      root.className = 'cmark off';
      const pip = document.createElement('span');
      pip.className = 'cpip';
      root.append(pip);
      this.compassEl.append(root);
      this.compassMarks.push({
        root,
        pip,
        lastX: Number.NaN,
        lastId: -1,
        exit: false,
        edge: false,
        shown: false,
      });
    }

    for (let i = 0; i < COMPASS_MAX_WAKERS; i++) {
      this.compassPick.push({ id: 0, dist: 0, rel: 0 });
    }

    for (const el of this.haloEl.querySelectorAll<HTMLSpanElement>('#halopips .pip')) {
      this.haloPipEls.push(el);
    }

    for (let i = 0; i < HALO_DMG_POOL; i++) {
      const el = document.createElement('span');
      el.className = 'dnum';
      this.haloDmgEl.append(el);
      this.haloDmgPool.push(el);
    }

    // Both arcs are drawn full and walked back with the dash offset, so the paint is one
    // property write and never a path rebuild.
    if (this.haloHpEl) this.haloHpEl.style.strokeDasharray = HALO_ARC_LEN.toFixed(1);
    if (this.haloStamEl) this.haloStamEl.style.strokeDasharray = HALO_ARC_LEN.toFixed(1);
  }

  /**
   * The one place the halo's state is decided. Pure: the same inputs always give the same
   * answer, so the ring can never latch on. `gone` beats everything — down the sights, and
   * whenever the raid HUD is not live at all (hub, death screen, kill cam).
   */
  private haloStateFor(nowMs: number, health: number): HaloState {
    if (this.haloForced !== null) return this.haloForced;
    if (!this.haloLive || this.haloAiming) return 'gone';
    if (health > 0 && health < HALO_LOW_HP) return 'awake';
    return nowMs < this.haloWakeUntil ? 'awake' : 'asleep';
  }

  /**
   * Wakes the ring for HALO_WAKE_MS. Called on damage taken, a weapon swap or reload, an
   * ability coming off cooldown and a sprint that is actually draining stamina. Low health
   * does not go through here: it holds the ring awake for as long as it lasts, which is a
   * question `haloStateFor` asks directly rather than a latch that could expire under it.
   */
  wakeHalo(): void {
    this.haloWakeUntil = performance.now() + HALO_WAKE_MS;
  }

  /**
   * Forces a state and freezes it there, for screenshots and the console handle. `null`
   * hands the ring back to the state machine.
   */
  forceHaloState(state: HaloState | null): void {
    this.haloForced = state;
  }

  /** ADS. Written every frame from the same Buttons.Aim bit the view model reads. */
  setAiming(aiming: boolean): void {
    if (aiming !== this.haloAiming) {
      // Down the sights the iron sight is the reticle: the HUD crosshair sat on top of the
      // wick beads and hid what the player was aiming at (Chris, 2026-09-05).
      this.crosshairEl.classList.toggle('ads', aiming);
    }
    this.haloAiming = aiming;
  }

  /** Health and stamina arcs, 0..1 each. Quantised: 1/128 of an arc is under a pixel. */
  private setHaloArcs(hpFrac: number, stamFrac: number): void {
    const hp = Math.round(clamp(hpFrac, 0, 1) * 128) / 128;
    if (hp !== this.haloHpFrac) {
      this.haloHpFrac = hp;
      if (this.haloHpEl) {
        this.haloHpEl.style.strokeDashoffset = (HALO_ARC_LEN * (1 - hp)).toFixed(1);
      }
    }
    const st = Math.round(clamp(stamFrac, 0, 1) * 128) / 128;
    if (st !== this.haloStamFrac) {
      this.haloStamFrac = st;
      if (this.haloStamEl) {
        this.haloStamEl.style.strokeDashoffset = (HALO_ARC_LEN * (1 - st)).toFixed(1);
      }
    }
  }

  /** One ability pip at the bottom of the ring. Latched per slot. */
  setHaloPip(slot: number, ready: boolean): void {
    if (this.haloPipReady[slot] === ready) return;
    this.haloPipReady[slot] = ready;
    this.haloPipEls[slot]?.classList.toggle('ready', ready);
  }

  /** A damage number by the reticle, in red. Pooled round-robin; never allocates. */
  showDamageNumber(amount: number): void {
    const el = this.haloDmgPool[this.haloDmgNext];
    this.haloDmgNext = (this.haloDmgNext + 1) % this.haloDmgPool.length;
    if (!el) return;
    el.textContent = `-${String(Math.max(1, Math.round(amount)))}`;
    // Scatter them a little so four hits in a burst do not print on top of each other.
    el.style.left = `${String(50 + (this.haloDmgNext % 3) * 7 - 7)}%`;
    el.classList.remove('on');
    void el.offsetWidth; // restart the keyframe
    el.classList.add('on');
  }

  /**
   * One frame of the halo. `yaw` is the camera yaw in radians (0 faces −Z, north; positive
   * turns left — see the shared sim), `viewer` the player's feet, and `wakers` /
   * `wakerCount` the buffer RemoteManager.compassTargets just filled. `nameOf` is unused by
   * the arc now that the labels live on #haloexit, but stays in the signature because the
   * caller already has it and a future Waker label will want it.
   *
   * `on` is false in the hub, on the death screen and under the kill cam.
   */
  updateHalo(
    on: boolean,
    nowMs: number,
    health: number,
    hpFrac: number,
    stamFrac: number,
    yaw: number,
    viewer: Vec3,
    wakers: readonly CompassTarget[],
    wakerCount: number,
  ): void {
    this.haloLive = on;
    const state = this.haloStateFor(nowMs, health);
    if (state !== this.haloState) {
      this.haloState = state;
      this.haloEl.className = state;
    }
    if (state === 'gone') {
      // Nothing under a zero opacity is worth a style write.
      return;
    }
    this.setHaloArcs(hpFrac, stamFrac);

    // A world heading h (0 = north) sits at h + yaw relative to the camera, positive to
    // the right: yaw 0 faces −Z, and turning left (yaw up) walks north to the right. The
    // ±90° of bearing the arc reads is squeezed onto ±COMPASS_ARC_DEG of the ring.
    const perDeg = COMPASS_ARC_DEG / COMPASS_HALF_SPAN_DEG;
    const yawDeg = yaw * DEG;
    for (let i = 0; i < this.compassCards.length; i++) {
      const card = this.compassCards[i];
      if (!card) continue;
      const rel = normDeg(i * 45 + yawDeg);
      if (Math.abs(rel) > COMPASS_HALF_SPAN_DEG + COMPASS_CARD_SLACK_DEG) {
        if (card.shown) {
          card.shown = false;
          card.el.classList.add('off');
        }
        continue;
      }
      if (!card.shown) {
        card.shown = true;
        card.el.classList.remove('off');
      }
      const deg = Math.round(rel * perDeg * 2) / 2;
      if (deg !== card.lastX) {
        card.lastX = deg;
        card.el.style.transform = arcTransform(deg);
      }
    }

    // Markers, in priority order: the live extracts first (they own the mint), then the
    // nearest Wakers. The nearest extract also writes the label above the ring.
    let m = 0;
    let bestExitId = 0;
    let bestExitM = 0;
    let bestExitDist = Number.POSITIVE_INFINITY;
    for (let i = 0; i < this.compassExitCount; i++) {
      const x = this.compassExits[i];
      if (!x) continue;
      const dx = x.x - viewer.x;
      const dy = x.y - viewer.y;
      const dz = x.z - viewer.z;
      const dist = Math.hypot(dx, dy, dz);
      if (dist < bestExitDist) {
        bestExitDist = dist;
        bestExitId = x.id;
        bestExitM = Math.round(dist);
      }
      m = this.writeMark(m, true, x.id, this.compassBearing(dx, dz, yaw), perDeg);
    }
    const exitLabel =
      bestExitId === 0
        ? ''
        : `${this.extractNames.get(bestExitId) ?? `exit ${String(bestExitId)}`} · ${String(bestExitM)} m`;
    if (exitLabel !== this.lastHaloExit) {
      this.lastHaloExit = exitLabel;
      this.haloExitEl.textContent = exitLabel;
    }

    let picked = 0;
    for (let i = 0; i < wakerCount; i++) {
      const t = wakers[i];
      if (!t) continue;
      const dx = t.pos.x - viewer.x;
      const dy = t.pos.y - viewer.y;
      const dz = t.pos.z - viewer.z;
      const dist = Math.hypot(dx, dy, dz);
      const worst = this.compassPick[COMPASS_MAX_WAKERS - 1];
      if (picked === COMPASS_MAX_WAKERS && worst && dist >= worst.dist) continue;
      // Insertion into the fixed buffer: when it is full the far end falls off the back.
      let j = picked < COMPASS_MAX_WAKERS ? picked : COMPASS_MAX_WAKERS - 1;
      while (j > 0) {
        const prev = this.compassPick[j - 1];
        const here = this.compassPick[j];
        if (!prev || !here || prev.dist <= dist) break;
        here.id = prev.id;
        here.dist = prev.dist;
        here.rel = prev.rel;
        j -= 1;
      }
      const slot = this.compassPick[j];
      if (!slot) continue;
      slot.id = t.id;
      slot.dist = dist;
      slot.rel = this.compassBearing(dx, dz, yaw);
      if (picked < COMPASS_MAX_WAKERS) picked += 1;
    }
    for (let i = 0; i < picked; i++) {
      const p = this.compassPick[i];
      if (!p) continue;
      m = this.writeMark(m, false, p.id, p.rel, perDeg);
    }

    for (; m < this.compassMarks.length; m++) {
      const mark = this.compassMarks[m];
      if (!mark || !mark.shown) continue;
      mark.shown = false;
      mark.root.classList.add('off');
    }
  }

  /** The map's own names for its extracts, so the halo says "The Ferry", not "exit 1". */
  setExtractNames(defs: readonly { id: number; name: string }[] | undefined): void {
    this.extractNames.clear();
    for (const d of defs ?? []) this.extractNames.set(d.id, d.name);
  }

  /**
   * The live extracts, out of the latest `ExtractSnap` list. Only the lit ones make the
   * arc: an unlit extract is somewhere to remember, not somewhere to run. Called at most a
   * couple of times a second, so the small copy here keeps the frame loop reading plain
   * numbers.
   */
  setCompassExtracts(list: readonly ExtractSnap[]): void {
    let n = 0;
    for (const x of list) {
      if (!x.active) continue;
      if (n >= COMPASS_MAX_EXITS) break;
      let slot = this.compassExits[n];
      if (!slot) {
        slot = { id: 0, x: 0, y: 0, z: 0 };
        this.compassExits.push(slot);
      }
      slot.id = x.id;
      slot.x = x.pos.x;
      slot.y = x.pos.y;
      slot.z = x.pos.z;
      n += 1;
    }
    this.compassExitCount = n;
  }

  /**
   * Bearing of (dx, dz) relative to where the camera is looking, in degrees, positive to
   * the right. Camera forward is (−sin yaw, −cos yaw) and camera right is (cos yaw, −sin yaw).
   */
  private compassBearing(dx: number, dz: number, yaw: number): number {
    const s = Math.sin(yaw);
    const c = Math.cos(yaw);
    const along = -dx * s - dz * c;
    const side = dx * c - dz * s;
    return Math.atan2(side, along) * DEG;
  }

  /** Writes one pooled marker and returns the next free slot. Off-arc markers clamp to the
   * end and dim: the bearing is still "that way", it is just no longer a place. */
  private writeMark(index: number, exit: boolean, id: number, rel: number, perDeg: number): number {
    const mark = this.compassMarks[index];
    if (!mark) return index;
    const edge = Math.abs(rel) > COMPASS_HALF_SPAN_DEG;
    const deg = edge
      ? rel > 0
        ? COMPASS_ARC_DEG
        : -COMPASS_ARC_DEG
      : Math.round(rel * perDeg * 2) / 2;

    if (!mark.shown) {
      mark.shown = true;
      mark.root.classList.remove('off');
    }
    // A pooled slot can change hands between an extract and a Waker, and the two id spaces
    // overlap — so the kind is part of "is this still the same marker?" below.
    if (exit !== mark.exit) {
      mark.exit = exit;
      mark.root.classList.toggle('exit', exit);
      // An extract is a door out and gets the drawing that says so; a Waker keeps the
      // bare rotated square, which is the same pip the reticle uses. Written only when a
      // pooled slot changes hands, so the frame loop still allocates nothing.
      mark.pip.innerHTML = exit ? icon('extract', 13) : '';
    }
    if (edge !== mark.edge) {
      mark.edge = edge;
      mark.root.classList.toggle('edge', edge);
    }
    if (deg !== mark.lastX) {
      mark.lastX = deg;
      mark.root.style.transform = arcTransform(deg);
    }
    if (id !== mark.lastId) mark.lastId = id;
    return index + 1;
  }

  // ------------------------------------------------------------------ health

  setHealth(hp: number, maxHp: number): void {
    const clamped = Math.max(0, Math.min(maxHp, Math.round(hp)));
    if (clamped === this.lastHealth) return;
    this.lastHealth = clamped;
    const frac = maxHp > 0 ? clamped / maxHp : 0;
    this.healthFillEl.style.transform = `scaleX(${String(frac)})`;
    this.healthNumEl.textContent = String(clamped);
    // The bar and the number are no longer siblings (the number sits beside the whole
    // bar stack), so the hurt state is written to both rather than picked up by a
    // sibling selector.
    const hurt = frac <= 0.34;
    this.healthBarEl.classList.toggle('hurt', hurt);
    this.healthNumEl.classList.toggle('hurt', hurt);
  }

  /**
   * The Calling name on the state line under the vitals ("Warden · crouched"). '' clears
   * it, which is what a player with no Calling chosen gets.
   */
  setCallingName(name: string): void {
    if (name === this.lastCallingName) return;
    this.lastCallingName = name;
    this.callingNameEl.textContent = name;
  }

  // --------------------------------------------------------------- movement

  /**
   * Stamina bar, winded screen effect, and the skip-jump tick. Sourced from the Predictor's
   * local state every frame — never from server snapshots — so it tracks input with zero lag.
   * `nowMs` should be a monotonic clock (performance.now() or the frame's rAF timestamp).
   */
  updateMovement(state: MovementSnapshot, nowMs: number): void {
    const frac = clamp(state.stamina / MAX_STAMINA, 0, 1);
    this.staminaFillEl.style.transform = `scaleX(${String(frac)})`;
    this.staminaBarEl.classList.toggle('winded', state.winded);
    this.windedFxEl.classList.toggle('on', state.winded);

    // Stance glyph next to the stamina bar — standing/crouched/prone. Movement penalties
    // are meant to be felt rather than read, so this is the only place stance is spelled
    // out; no numbers ever go on screen for it.
    if (state.stance !== this.lastStance) {
      this.lastStance = state.stance;
      this.stanceIconEl.dataset.stance = String(state.stance);
    }

    if (state.stamina >= MAX_STAMINA) {
      if (this.staminaFullSince < 0) this.staminaFullSince = nowMs;
      this.staminaBarEl.classList.toggle('idle', nowMs - this.staminaFullSince >= STAMINA_IDLE_MS);
    } else {
      this.staminaFullSince = -1;
      this.staminaBarEl.classList.remove('idle');
    }

    // Skip-jump: grounded within the landing window last frame, now rising with more
    // horizontal speed than we had a moment ago.
    const hspeed = Math.hypot(state.vel.x, state.vel.z);
    const wasInSkipWindow = this.prevGrounded && this.prevGroundTicks <= SKIP_WINDOW_TICKS;
    if (wasInSkipWindow && state.vel.y > 0 && hspeed > this.prevHSpeed) {
      this.skipFlashUntil = nowMs + SKIP_FLASH_MS;
      this.skipTickEl.classList.add('show');
    } else if (this.skipFlashUntil > 0 && nowMs >= this.skipFlashUntil) {
      this.skipFlashUntil = 0;
      this.skipTickEl.classList.remove('show');
    }
    this.prevGrounded = state.grounded;
    this.prevGroundTicks = state.groundTicks;
    this.prevHSpeed = hspeed;

    // SPRINT: draining stamina *while actually moving fast*. Stamina alone is not enough —
    // every ability with a cost drains it too, and a SPRINT mark that lights when you cast
    // Adrenal Surge standing still is a liar. Latched, so the line only touches the DOM
    // when the state flips.
    const sprinting =
      !state.winded && state.stamina < this.prevStamina - 0.01 && hspeed > WALK_SPEED * 1.05;
    if (sprinting !== this.sprintOn) {
      this.sprintOn = sprinting;
      this.sprintTickEl.classList.toggle('show', sprinting);
    }
    // Stamina draining under a real sprint is one of the halo's wake events. Latched to
    // the same flag, which already excludes the drain an ability's stamina cost makes —
    // a ring that wakes when you cast Adrenal Surge standing still is a liar too.
    if (sprinting) this.wakeHalo();
    this.prevStamina = state.stamina;
  }

  // ----------------------------------------------------------------- effects

  /**
   * A hit taken: the decaying red edge vignette, one short red pulse over the top of it,
   * the damage number by the reticle, and the ring woken for three seconds. `amount` is
   * 0..1 of the screen-edge intensity; `damage` is the hit points, for the number.
   */
  flashDamage(amount: number, damage = 0): void {
    this.vignette = Math.min(1, Math.max(this.vignette, amount));
    this.vignetteEl.style.opacity = String(this.vignette);
    this.hitPulseEl.classList.remove('on');
    void this.hitPulseEl.offsetWidth; // restart the keyframe
    this.hitPulseEl.classList.add('on');
    if (damage > 0) this.showDamageNumber(damage);
    this.wakeHalo();
  }

  showHitMarker(headshot: boolean): void {
    this.hitMarkerUntil = performance.now() + HIT_MARKER_MS;
    this.hitMarkerEl.classList.toggle('head', headshot);
    this.hitMarkerEl.classList.add('show');
  }

  /** Decays the timed overlays. Call once per frame with the frame delta in seconds. */
  tickEffects(dt: number, nowMs: number): void {
    if (this.vignette > 0) {
      this.vignette = Math.max(0, this.vignette - dt * 1.4);
      this.vignetteEl.style.opacity = String(this.vignette);
    }
    if (this.hitMarkerUntil > 0 && nowMs >= this.hitMarkerUntil) {
      this.hitMarkerUntil = 0;
      this.hitMarkerEl.classList.remove('show', 'head');
    }
  }

  // --------------------------------------------------------------- world clock

  /**
   * The named hour, top-centre, in the slot the raid clock left behind.
   *
   * Driven by `packages/shared/src/worldclock.ts` — the same pure function of wall time the sky
   * reads — so the words and the light can never disagree. There is no message for this and
   * nothing to synchronise: two machines whose clocks agree to the second agree on the hour.
   *
   * Written at 1 Hz. The phrase changes eight times in four hours and the hairline moves a
   * pixel a minute; asking the DOM about either sixty times a second buys nothing.
   */
  updateWorldClock(visible: boolean, nowMs: number): void {
    if (this.worldClockOn !== visible) {
      this.worldClockOn = visible;
      this.worldClockEl.classList.toggle('on', visible);
    }
    if (!visible) return;
    if (nowMs - this.worldClockAt < WORLD_CLOCK_MS) return;
    this.worldClockAt = nowMs;

    const t = timeOfDay(Date.now());
    const phrase = dayPhrase(t);
    if (phrase !== this.worldClockPhrase) {
      this.worldClockPhrase = phrase;
      this.worldClockPhraseEl.textContent = phrase;
    }
    const night = clockGlyph(t) === 'moon';
    if (night !== this.worldClockNight) {
      this.worldClockNight = night;
      this.worldClockEl.classList.toggle('night', night);
    }
    // Quantised to the pixel the 208-wide plate can actually show: a scaleX write per frame
    // for a bar that crosses in four hours is a style recalculation for nothing.
    const step = Math.round(t * 200) / 200;
    if (step !== this.worldClockFill) {
      this.worldClockFill = step;
      this.worldClockFillEl.style.transform = `scaleX(${step.toFixed(3)})`;
    }
  }

  // ---------------------------------------------------------------- interact

  /**
   * Something is in reach. The touch USE button this used to light went with the mobile
   * layer; the prompt in ui/overlay.tsx is the only tell now, so this is a no-op the host
   * still calls — kept rather than ripped out of five call sites in main.ts.
   */
  setInteractAvailable(on: boolean): void {
    void on;
  }

  /**
   * The last still grabbed for the hub backdrop, kept so the deploy cinematic has something of
   * this player's own world to show on a ground whose painted plate has not been generated yet.
   */
  lastBackdropUrl: string | null = null;

  /** Paints the frozen still the hub sits over. `null` falls back to the CSS gradient. */
  setHubBackdrop(dataUrl: string | null): void {
    this.lastBackdropUrl = dataUrl;
    this.hubBackdrop.style.backgroundImage = dataUrl === null ? '' : `url("${dataUrl}")`;
  }

  // ------------------------------------------------------------------- death

  showDeath(killerName: string | null): void {
    this.deathTitleEl.textContent = '';
    if (killerName === null) {
      this.deathTitleEl.textContent = 'Dropped';
    } else {
      const who = document.createElement('span');
      who.className = 'killer';
      who.textContent = killerName;
      this.deathTitleEl.append(document.createTextNode('Dropped by '), who);
    }
    this.deathCountEl.textContent = 'respawning…';
    this.deathEl.classList.add('on');
  }

  /** `seconds` is the remaining wait; call each frame while dead. */
  setRespawnCountdown(seconds: number): void {
    const s = Math.max(0, Math.ceil(seconds));
    this.deathCountEl.textContent = s > 0 ? `respawning in ${String(s)}` : 'respawning…';
  }

  hideDeath(): void {
    this.deathEl.classList.remove('on');
  }

  // ---------------------------------------------------------------- kill cam

  /**
   * The card that rides along with the kill-cam replay. `distanceM` is the gap between
   * killer and victim at the moment of the kill; `weapon` is already a display name.
   */
  showKillCam(killerName: string, weapon: string, distanceM: number, headshot: boolean): void {
    this.killCamTitleEl.textContent = 'Killed by ';
    const who = document.createElement('span');
    who.className = 'who';
    who.textContent = killerName;
    this.killCamTitleEl.append(who);

    this.killCamMetaEl.textContent = `${weapon} · ${distanceM.toFixed(1)} m`;
    if (headshot) {
      const hs = document.createElement('span');
      hs.className = 'hs';
      hs.textContent = ' · headshot';
      this.killCamMetaEl.append(hs);
    }

    this.killCamProgress = -1;
    this.setKillCamProgress(0);
    this.killCamEl.classList.add('on');
    // Our own reticle and ammo have no business over somebody else's viewpoint.
    document.body.classList.add('killcam');
  }

  /** `fraction` is how far through the replay we are, 0..1. Called every frame. */
  setKillCamProgress(fraction: number): void {
    // Quantised so a 144 Hz frame loop is not writing a fresh transform every frame.
    const q = Math.round(clamp(fraction, 0, 1) * 64) / 64;
    if (q === this.killCamProgress) return;
    this.killCamProgress = q;
    this.killCamFillEl.style.transform = `scaleX(${String(q)})`;
  }

  hideKillCam(): void {
    this.killCamEl.classList.remove('on');
    document.body.classList.remove('killcam');
  }

  // -------------------------------------------------------- M3: abilities

  /**
   * The one active ability grade, or `null` for none. Only one is ever up: Veil, Surge
   * and Silence are mutually exclusive in practice, and stacking two full-screen washes
   * would make the world unreadable.
   */
  setAbilityGrade(grade: 'surge' | 'veil' | 'silence' | null): void {
    if (grade === this.abilityGrade) return;
    this.abilityGrade = grade;
    this.abilityFxEl.classList.remove('surge', 'veil', 'silence');
    if (grade !== null) this.abilityFxEl.classList.add(grade);
  }

  // ------------------------------------------------------ M3: downed & revive

  /**
   * `secondsLeft` is the remaining downed clock; `null` leaves the state. The bar drains
   * left-to-right over DOWNED_DURATION_MS so the number never has to be read.
   */
  setDowned(secondsLeft: number | null, totalSeconds: number): void {
    const on = secondsLeft !== null;
    if (on !== this.downedOn) {
      this.downedOn = on;
      this.downedEl.classList.toggle('on', on);
      this.downedFxEl.classList.toggle('on', on);
      if (on) this.downedSubEl.textContent = 'hold to call for help';
    }
    if (secondsLeft === null) return;
    const frac = totalSeconds > 0 ? clamp(secondsLeft / totalSeconds, 0, 1) : 0;
    this.downedFillEl.style.transform = `scaleX(${frac.toFixed(3)})`;
  }

  /**
   * The revive prompt over a downed squadmate. `progress` is 0..1 of the 3 s channel and
   * drives the ring; `null` for `name` hides the whole thing.
   */
  setRevive(name: string | null, progress: number): void {
    const on = name !== null;
    if (on !== this.reviveOn) {
      this.reviveOn = on;
      this.reviveEl.classList.toggle('on', on);
    }
    if (name === null) return;
    const key = document.body.classList.contains('touch') ? 'USE' : 'E';
    const label = `Hold ${key} to revive ${name}`;
    if (label !== this.lastReviveLabel) {
      this.lastReviveLabel = label;
      this.reviveTextEl.textContent = label;
    }
    if (this.reviveRingEl) {
      // r = 19 → circumference 119.4; the dash offset walks it back to zero.
      const offset = Math.round(REVIVE_RING_LEN * (1 - clamp(progress, 0, 1)) * 10) / 10;
      if (offset !== this.lastReviveOffset) {
        this.lastReviveOffset = offset;
        this.reviveRingEl.style.strokeDashoffset = String(offset);
      }
    }
  }

  // ----------------------------------------------------------- M3: boss bar

  /** `null` hides the bar. Shown whenever a Verger is in the snapshot. */
  setBoss(boss: { name: string; hp: number; maxHp: number } | null): void {
    const on = boss !== null;
    if (on !== this.bossOn) {
      this.bossOn = on;
      this.bossBarEl.classList.toggle('on', on);
    }
    if (!boss) return;
    if (boss.name !== this.lastBossName) {
      this.lastBossName = boss.name;
      this.bossNameEl.textContent = boss.name;
    }
    const frac = boss.maxHp > 0 ? clamp(boss.hp / boss.maxHp, 0, 1) : 0;
    const rounded = Math.round(frac * 200) / 200;
    if (rounded !== this.lastBossFrac) {
      this.lastBossFrac = rounded;
      this.bossFillEl.style.transform = `scaleX(${rounded.toFixed(3)})`;
    }
  }

  // ------------------------------------------------- Phase 2b: target frame

  /**
   * Whoever is selected. The boss bar's instrument pointed at `input.targetId` instead of at
   * the one scripted thing in the room — a Hymn with its kind and level, or another Waker
   * with their name and level, and the same health bar for both.
   *
   * `castMs` / `castName` drive its cast bar: `Ev.Cast` for a player, `EntitySnap.castEndsAt`
   * for a monster (the Cantor's wind-up), 0 for neither. That bar is the only warning a party
   * ever gets, so it goes up the frame the cast starts and comes down the frame it ends.
   */
  setTarget(target: TargetView | null): void {
    const on = target !== null;
    if (on !== this.targetOn) {
      this.targetOn = on;
      this.targetFrameEl.classList.toggle('on', on);
      if (!on) {
        // Latches reset with the frame, so re-selecting the same target repaints rather
        // than trusting a name that is no longer on screen.
        this.lastTargetName = '';
        this.lastTargetLvl = '';
        this.lastTargetFrac = -1;
        this.lastTargetCastName = '';
        this.lastTargetCastFrac = -1;
        this.targetCasting = false;
        this.targetFriendly = false;
        this.targetDead = false;
        this.targetFrameEl.classList.remove('casting', 'friendly', 'dead');
      }
    }
    if (!target) return;

    if (target.name !== this.lastTargetName) {
      this.lastTargetName = target.name;
      this.targetNameEl.textContent = target.name;
    }
    const lvl = target.level > 0 ? `LVL ${String(target.level)}` : '';
    if (lvl !== this.lastTargetLvl) {
      this.lastTargetLvl = lvl;
      this.targetLvlEl.textContent = lvl;
    }
    if (target.friendly !== this.targetFriendly) {
      this.targetFriendly = target.friendly;
      this.targetFrameEl.classList.toggle('friendly', target.friendly);
    }
    const isDead = target.hp <= 0;
    if (isDead !== this.targetDead) {
      this.targetDead = isDead;
      this.targetFrameEl.classList.toggle('dead', isDead);
    }

    const frac = target.maxHp > 0 ? clamp(target.hp / target.maxHp, 0, 1) : 0;
    const rounded = Math.round(frac * 200) / 200;
    if (rounded !== this.lastTargetFrac) {
      this.lastTargetFrac = rounded;
      this.targetFillEl.style.transform = `scaleX(${rounded.toFixed(3)})`;
    }

    const casting = target.castMs > 0;
    if (casting !== this.targetCasting) {
      this.targetCasting = casting;
      this.targetFrameEl.classList.toggle('casting', casting);
    }
    if (!casting) return;
    if (target.castName !== this.lastTargetCastName) {
      this.lastTargetCastName = target.castName;
      this.targetCastNameEl.textContent = target.castName;
    }
    const cf = Math.round(clamp(target.castFraction, 0, 1) * 200) / 200;
    if (cf !== this.lastTargetCastFrac) {
      this.lastTargetCastFrac = cf;
      this.targetCastFillEl.style.transform = `scaleX(${cf.toFixed(3)})`;
    }
  }

  // -------------------------------------------------------------- scoreboard

  setScoreboardVisible(on: boolean): void {
    this.scoreboardEl.classList.toggle('on', on);
  }

  setScores(rows: readonly ScoreRow[], selfId: number): void {
    const sorted = [...rows].sort((a, b) => b.kills - a.kills || a.deaths - b.deaths);
    this.scoreRowsEl.textContent = '';
    // "waker", not "drifter": a drifter is what an unnamed account is called, and heading the
    // name column with it read as a placeholder next to every other screen's "Waker".
    for (const label of ['waker', 'kills', 'deaths']) {
      const h = document.createElement('div');
      h.className = 'h';
      h.textContent = label;
      this.scoreRowsEl.append(h);
    }
    for (const r of sorted) {
      const n = document.createElement('div');
      n.className = r.id === selfId ? 'n me' : 'n';
      n.textContent = r.name;
      const k = document.createElement('div');
      k.className = 'v';
      k.textContent = String(r.kills);
      const d = document.createElement('div');
      d.className = 'v';
      d.textContent = String(r.deaths);
      this.scoreRowsEl.append(n, k, d);
    }
  }

  // ------------------------------------------------------------------- start

  loadName(): string {
    let stored = '';
    try {
      stored = localStorage.getItem(NAME_KEY) ?? '';
    } catch {
      /* private mode / disabled storage */
    }
    return stored;
  }

  saveName(name: string): void {
    try {
      localStorage.setItem(NAME_KEY, name);
    } catch {
      /* ignore */
    }
  }

  hideStart(): void {
    this.startModal.classList.add('hidden');
  }

  showStart(): void {
    this.startModal.classList.remove('hidden');
  }

  setBoot(text: string): void {
    this.bootLine.textContent = text;
  }

  // -------------------------------------------------------------------- stats

  setStats(s: StatsView): void {
    const ping = s.ping > 0 ? `${String(Math.round(s.ping))}ms` : '—';
    const aa = gfxStats.aa ? 'on' : 'off';
    this.statsEl.innerHTML =
      `<b>${String(Math.round(s.fps))}</b> fps  ·  ping <b>${ping}</b>\n` +
      `tick <b>${String(s.tick)}</b>  ·  players <b>${String(s.players)}</b>\n` +
      `gfx ${s.backend}\n` +
      `res ${String(gfxStats.drawW)}x${String(gfxStats.drawH)}  ·  ${gfxStats.scale.toFixed(2)}x  ·  aa ${aa}  ·  post ${gfxStats.post ? 'on' : 'off'}\n` +
      `cpu <b>${gfxStats.cpuMs >= 0 ? gfxStats.cpuMs.toFixed(1) : '—'}</b>ms  ·  draws <b>${String(gfxStats.draws)}</b>`;
  }

  // ------------------------------------------------------------------- banner

  setConnection(state: ConnState, detail: string): void {
    const el = this.bannerEl;
    el.classList.remove('bad', 'good');
    switch (state) {
      case 'connecting':
        el.textContent = 'connecting…';
        el.classList.add('show');
        break;
      case 'reconnecting':
        el.textContent = detail ? `reconnecting — ${detail}` : 'reconnecting…';
        el.classList.add('show');
        break;
      case 'kicked':
        el.textContent = `kicked: ${detail || 'no reason given'}`;
        el.classList.add('show', 'bad');
        break;
      case 'closed':
        el.textContent = 'disconnected';
        el.classList.add('show', 'bad');
        break;
      case 'open':
      case 'idle':
      default:
        el.classList.remove('show');
        el.textContent = '';
        break;
    }
  }

  // --------------------------------------------------------------------- chat

  /**
   * One chat line. `who` of null is the room talking (a system line). `down` marks a line
   * about somebody going down or dying, which the CSS paints red — dead is red, and it
   * always will be.
   */
  addChat(who: string | null, text: string, down = false): void {
    const line = document.createElement('div');
    line.className = who === null ? 'line sys' : 'line';
    if (down) line.classList.add('down');
    if (who === null) {
      // A system line is the room talking, not a player: it gets the Choir mark so the
      // eye can skip it (or find it) without reading a word.
      const mark = document.createElement('span');
      mark.className = 'sysic';
      mark.innerHTML = icon('choir', 12);
      line.append(mark, document.createTextNode(text));
    } else {
      const w = document.createElement('span');
      w.className = 'who';
      w.textContent = `${who}: `;
      line.append(w, document.createTextNode(text));
    }
    this.chatLogEl.append(line);
    while (this.chatLogEl.childElementCount > MAX_CHAT_LINES) {
      this.chatLogEl.firstElementChild?.remove();
    }
  }

  get chatOpen(): boolean {
    return this.chatInput.classList.contains('open');
  }

  openChat(): void {
    this.chatInput.classList.add('open');
    this.chatInput.value = '';
    this.chatInput.focus();
  }

  closeChat(): void {
    this.chatInput.classList.remove('open');
    this.chatInput.blur();
    this.chatInput.value = '';
  }
}
